/** @file Redo target (spec §8.2): the actor's newest live undo, unless a newer own edit came after it. */
import { undoneRevs } from '@pose2d/api/queries/undoneRevs';
import type { CommitSummary } from '@pose2d/api/types';

/** An undo reverts an ordinary commit (a target outside the feed counts as ordinary); a redo reverts an undo. */
const isUndo = (c: CommitSummary, byRev: ReadonlyMap<number, CommitSummary>): boolean =>
  c.revertOf !== null && (byRev.get(c.revertOf)?.revertOf ?? null) === null;

/** Walking the actor's commits newest first: an ordinary commit ends the search, redos and undone undos are skipped,
 *  the first live undo is the rev to revert (E8-R6); a repair commit is neither (W3-14); null when there is nothing to redo. */
export function redoTarget(feed: readonly CommitSummary[], actor: string): number | null {
  const undone = undoneRevs(feed);
  const byRev = new Map(feed.map((c): [number, CommitSummary] => [c.rev, c]));
  for (const c of feed.filter((x) => x.actor === actor && x.repair !== true).sort((a, b) => b.rev - a.rev)) {
    if (c.revertOf === null) return null;
    if (isUndo(c, byRev) && !undone.has(c.rev)) return c.rev;
  }
  return null;
}
